import DOMPurify from 'dompurify';

/** O que o `RichTextEditor` produz pela barra: parágrafo, títulos 1–3, ênfases, listas e citação. */
const TAGS_TEXTO_RICO = [
  'p',
  'br',
  'h1',
  'h2',
  'h3',
  'strong',
  'em',
  'u',
  's',
  'ul',
  'ol',
  'li',
  'blockquote',
] as const;

const BLOCOS_ALINHAVEIS = new Set(['P', 'H1', 'H2', 'H3']);
const ALINHAMENTOS = new Set(['left', 'center', 'right', 'justify']);
const INICIO_DE_LISTA = /^\d{1,6}$/;

// O DOMPurify aceita ou recusa o atributo `style` inteiro, sem olhar o CSS. Aqui só sobrevive o
// alinhamento da barra: `url()` em qualquer outra propriedade faria requisição na tela de quem lê.
function reduzirAtributos(elemento: HTMLElement): void {
  const alinhamento = elemento.style.textAlign;
  elemento.removeAttribute('style');
  if (BLOCOS_ALINHAVEIS.has(elemento.tagName) && ALINHAMENTOS.has(alinhamento)) {
    elemento.style.textAlign = alinhamento;
  }
  const inicio = elemento.getAttribute('start');
  if (inicio !== null && !(elemento.tagName === 'OL' && INICIO_DE_LISTA.test(inicio))) {
    elemento.removeAttribute('start');
  }
}

/**
 * HTML do editor rico reduzido ao vocabulário dele, para exibir o que foi publicado. O backend já
 * sanitiza na gravação; isto é a segunda barreira, na tela. Fora do browser devolve vazio: sem
 * DOM, o DOMPurify devolveria o HTML como veio.
 *
 * @example sanitizarHtmlTextoRico('<p onclick="x()">Oi</p><script>alert(1)</script>') // '<p>Oi</p>'
 */
export function sanitizarHtmlTextoRico(html: string): string {
  if (typeof window === 'undefined') return '';
  const fragmento = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [...TAGS_TEXTO_RICO],
    ALLOWED_ATTR: ['style', 'start'],
    RETURN_DOM_FRAGMENT: true,
  });
  fragmento.querySelectorAll<HTMLElement>('[style], [start]').forEach(reduzirAtributos);
  const recipiente = document.createElement('div');
  recipiente.append(fragmento);
  return recipiente.innerHTML;
}
